import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import {
  AppInput,
  AppPhoneInput,
  AppSingleSelect,
  AppButton,
  AppConfirmDialog,
  AppBaseDropdown,
  extractPhoneDigits,
} from "@pro-sto/ui";
import type { SelectOption } from "@pro-sto/ui";
import {
  authStore,
  employeePositionsStore,
  employeesStore,
  employeePaymentSchemeStore,
  paymentSchemeTemplatesStore,
  toastStore,
} from "@/stores";
import { ROUTES } from "@/constants/routes";
import { adminEmployeesGetOne } from "@/services/employeesApi";
import type { PaymentSchemeRateType } from "@/types/employee.types";
import type { PaymentDraft } from "@/stores/EmployeePaymentSchemeStore";
import { PaymentSchemeEditor } from "@/components/PaymentSchemeEditor";
import {
  ArrowLeftIcon,
  FloppyDiskIcon,
  GearIcon,
  DotsThreeVerticalIcon,
  PencilIcon,
  PlusIcon,
} from "@/components/icons";
import "./EmployeeEditPage.css";

interface FormState {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  positionUuid: string | null;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  positionUuid?: string;
}

const emptyForm: FormState = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  positionUuid: null,
};

const NO_TEMPLATE_OPTION: SelectOption = { label: "Без шаблона", value: null };
const emptyPaymentDraft: PaymentDraft = { services: {} };

const EmployeeEditPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const isCreate = !uuid;
  const serviceCenterUuid = authStore.user?.service_center_uuid;

  const [isLoadingEmployee, setIsLoadingEmployee] = useState(!isCreate);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTemplateMenuOpen, setIsTemplateMenuOpen] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState<PaymentDraft>(emptyPaymentDraft);
  // undefined — диалог закрыт; иначе — опция шаблона, которую применим после
  // подтверждения (несохранённые правки ставок иначе тихо потеряются).
  const [pendingTemplateOption, setPendingTemplateOption] = useState<SelectOption | null | undefined>(undefined);

  useEffect(() => {
    if (!serviceCenterUuid) return;
    employeePositionsStore.loadPositions(serviceCenterUuid);
    paymentSchemeTemplatesStore.setServiceCenterUuid(serviceCenterUuid);
  }, [serviceCenterUuid]);

  // submitError переживает навигацию (общее поле стора) — без сброса при
  // входе на страницу здесь всплывает ошибка от предыдущей неудачной
  // попытки создания/сохранения другого сотрудника.
  useEffect(() => {
    employeesStore.clearSubmitError();
    return () => employeesStore.clearSubmitError();
  }, []);

  useEffect(() => {
    if (isCreate || !uuid) {
      setForm(emptyForm);
      return;
    }

    let cancelled = false;
    setIsLoadingEmployee(true);
    adminEmployeesGetOne(uuid)
      .then((data) => {
        if (cancelled) return;
        setForm({
          firstName: data.first_name,
          lastName: data.last_name,
          // Бэкенд хранит телефон как "+7XXXXXXXXXX", форма/маска работают
          // с голыми 10 цифрами — приводим к общему для формы виду.
          phone: extractPhoneDigits(data.phone),
          email: data.email ?? "",
          positionUuid: data.position.uuid,
        });
      })
      .catch(() => {
        if (cancelled) return;
        toastStore.showError("Не удалось загрузить сотрудника");
        navigate(-1);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEmployee(false);
      });

    employeePaymentSchemeStore.loadScheme(uuid);
    setPaymentDraft(emptyPaymentDraft);

    return () => {
      cancelled = true;
      employeePaymentSchemeStore.reset();
    };
  }, [uuid, isCreate, navigate]);

  const handleFieldChange = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "Обязательное поле";
    if (!form.lastName.trim()) newErrors.lastName = "Обязательное поле";
    if (!/^\d{10}$/.test(form.phone))
      newErrors.phone = "Введите корректный номер телефона";
    if (!form.positionUuid) newErrors.positionUuid = "Обязательное поле";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => navigate(-1);

  const handleSave = async () => {
    if (!validate() || !serviceCenterUuid) return;

    const payload = {
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      // Контракт бэкенда требует телефон в формате "+7XXXXXXXXXX" —
      // form.phone хранит голые 10 цифр (см. AppPhoneInput).
      phone: `+7${form.phone}`,
      email: form.email.trim() || null,
      position_uuid: form.positionUuid as string,
    };

    if (isCreate) {
      const success = await employeesStore.createEmployee({
        ...payload,
        service_center_uuid: serviceCenterUuid,
      });
      if (!success) return;
      navigate(-1);
      return;
    }

    const success = await employeesStore.updateEmployee(uuid as string, payload);
    if (!success) return;

    // Схема оплаты копится в черновике (paymentDraft) и уходит на бэк
    // только здесь, по кнопке «Сохранить» — не по мере ввода. Тост об
    // успехе показываем только после того, как сохранилось и то, и
    // другое — иначе при падении пакета ставок пользователь уже увидел
    // бы «Изменения сохранены» при частично сохранённых данных.
    const remainingDraft = await employeePaymentSchemeStore.savePendingChanges(paymentDraft);
    if (remainingDraft) {
      setPaymentDraft(remainingDraft);
      return;
    }

    setPaymentDraft(emptyPaymentDraft);
    toastStore.showSuccess('Изменения сохранены');
    navigate(-1);
  };

  const scheme = employeePaymentSchemeStore.scheme;

  const templateOptions: SelectOption[] = [
    NO_TEMPLATE_OPTION,
    ...paymentSchemeTemplatesStore.activeOptions,
  ];
  const templateValue: SelectOption = scheme?.parent_template_uuid
    ? {
        label: scheme.parent_template_name ?? "",
        value: scheme.parent_template_uuid,
      }
    : NO_TEMPLATE_OPTION;

  const hasPendingPaymentDraft =
    paymentDraft.fixPerShift !== undefined ||
    paymentDraft.hourlyRate !== undefined ||
    Object.keys(paymentDraft.services).length > 0;

  const applyTemplateChange = async (option: SelectOption | null) => {
    const templateUuid = (option?.value as string | null) ?? null;
    // keep_overrides: true (дефолт по контракту) — ручные фикс/час/строки
    // услуг сохраняются, шаблон подставляется только туда, где ставок не было.
    const success = await employeePaymentSchemeStore.confirmApplyTemplate(templateUuid, true);
    // Применение шаблона — самостоятельное немедленное действие поверх
    // серверной схемы; несохранённый черновик ручных правок относился к
    // старой схеме и после подстановки шаблона уже не актуален.
    if (success) setPaymentDraft(emptyPaymentDraft);
  };

  const handleTemplateChange = async (option: SelectOption | null) => {
    const templateUuid = (option?.value as string | null) ?? null;
    if (templateUuid === (scheme?.parent_template_uuid ?? null)) return;

    if (hasPendingPaymentDraft) {
      // Несохранённые правки ставок улетят вместе с подстановкой шаблона
      // без предупреждения — спрашиваем подтверждение до вызова apply.
      setPendingTemplateOption(option ?? NO_TEMPLATE_OPTION);
      return;
    }

    await applyTemplateChange(option);
  };

  const handleConfirmTemplateChange = async () => {
    if (pendingTemplateOption === undefined) return;
    // Диалог держим открытым, пока запрос не отработает (как handleConfirmDelete
    // ниже) — иначе isApplyingTemplate/confirmingText в AppConfirmDialog не
    // успевают отрисоваться, а пользователь может тем временем кликнуть
    // «Сохранить» и запустить второе изменение схемы оплаты параллельно.
    await applyTemplateChange(pendingTemplateOption);
    setPendingTemplateOption(undefined);
  };

  const handleConfirmDelete = async () => {
    if (!uuid) return;
    setIsDeleting(true);
    const success = await employeesStore.deleteEmployee(uuid);
    setIsDeleting(false);
    if (success) {
      navigate(-1);
    } else {
      setIsDeleteModalOpen(false);
    }
  };

  const handleOpenTemplateEditor = () => {
    if (scheme?.parent_template_uuid) {
      navigate(
        generatePath(ROUTES.PAYMENT_SCHEME_TEMPLATE_EDIT, {
          uuid: scheme.parent_template_uuid,
        }),
      );
    } else {
      navigate(ROUTES.PAYMENT_SCHEME_TEMPLATE_NEW);
    }
  };

  const handleCreateNewTemplate = () => {
    navigate(ROUTES.PAYMENT_SCHEME_TEMPLATE_NEW);
  };

  if (isLoadingEmployee) {
    return (
      <div className="employee-edit-page" data-testid="employee-edit-page">
        <div className="employee-edit-page__container">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="employee-edit-page" data-testid="employee-edit-page">
      <div className="employee-edit-page__container">
        <div className="employee-edit-page__header">
          <div className="employee-edit-page__header-left">
            <button
              type="button"
              className="employee-edit-page__back-button"
              onClick={handleCancel}
              aria-label="Назад"
              data-testid="employee-edit-back-button"
            >
              <ArrowLeftIcon size={20} />
            </button>
            <h1 className="employee-edit-page__title">
              {isCreate ? "Добавление сотрудника" : "Редактирование сотрудника"}
            </h1>
          </div>
          <div className="employee-edit-page__header-actions">
            <AppButton
              variant="secondary"
              onClick={handleCancel}
              data-testid="employee-edit-cancel"
            >
              Отменить
            </AppButton>
            <AppButton
              variant="primary"
              iconRight={<FloppyDiskIcon size={16} />}
              onClick={handleSave}
              loading={
                employeesStore.isSaving ||
                employeePaymentSchemeStore.isSavingDraft ||
                employeePaymentSchemeStore.isApplyingTemplate
              }
              data-testid="employee-edit-save"
            >
              Сохранить
            </AppButton>
            {!isCreate && (
              <AppButton
                variant="secondary"
                onlyIcon
                iconLeft={<DotsThreeVerticalIcon size={16} />}
                aria-label="Ещё"
                onClick={() => setIsDeleteModalOpen(true)}
                data-testid="employee-edit-more"
              />
            )}
          </div>
        </div>

        <section className="employee-edit-page__section">
          <h2 className="employee-edit-page__section-title">Личные данные</h2>
          {employeesStore.submitError && (
            <p className="employee-edit-page__submit-error">
              {employeesStore.submitError}
            </p>
          )}
          <div className="employee-edit-page__personal-fields">
            <AppInput
              label="Имя"
              placeholder="Иван"
              value={form.firstName}
              onChange={(value) => handleFieldChange("firstName", value)}
              error={errors.firstName}
              required
              data-testid="employee-edit-first-name"
            />
            <AppInput
              label="Фамилия"
              placeholder="Иванов"
              value={form.lastName}
              onChange={(value) => handleFieldChange("lastName", value)}
              error={errors.lastName}
              required
              data-testid="employee-edit-last-name"
            />
            <AppPhoneInput
              value={form.phone}
              onChange={(value) => handleFieldChange("phone", value)}
              error={errors.phone}
              required
              data-testid="employee-edit-phone"
            />
            <AppInput
              label="Почта"
              placeholder="inbox@mail.ru"
              value={form.email}
              onChange={(value) => handleFieldChange("email", value)}
              data-testid="employee-edit-email"
            />
            <AppSingleSelect
              label="Должность"
              placeholder="Выберите должность"
              options={employeePositionsStore.options}
              value={
                employeePositionsStore.options.find(
                  (o) => o.value === form.positionUuid,
                ) ?? null
              }
              onChange={(option) =>
                handleFieldChange(
                  "positionUuid",
                  (option?.value as string | null) ?? null,
                )
              }
              error={errors.positionUuid}
              required
              data-testid="employee-edit-position"
            />
          </div>
        </section>

        {!isCreate && (
          <>
            <section className="employee-edit-page__section">
              <h2 className="employee-edit-page__section-title">
                Шаблон оплаты
              </h2>
              <div className="employee-edit-page__template-row">
                <div className="employee-edit-page__template-select">
                  <AppSingleSelect
                    options={templateOptions}
                    value={templateValue}
                    onChange={handleTemplateChange}
                    disabled={employeePaymentSchemeStore.isApplyingTemplate}
                    data-testid="employee-edit-template-select"
                  />
                </div>
                <AppBaseDropdown
                  opened={isTemplateMenuOpen}
                  onClose={() => setIsTemplateMenuOpen(false)}
                  dropdownWidth="220px"
                  xDirection="right"
                  yDirection="bottom"
                  toggle={
                    <button
                      type="button"
                      className="employee-edit-page__gear-button"
                      onClick={() => setIsTemplateMenuOpen((prev) => !prev)}
                      title="Управление шаблоном"
                      data-testid="employee-edit-template-gear"
                    >
                      <GearIcon size={20} />
                    </button>
                  }
                  dropdown={
                    <div className="employee-edit-page__template-menu">
                      <button
                        type="button"
                        className="employee-edit-page__template-menu-item"
                        onClick={() => {
                          setIsTemplateMenuOpen(false);
                          handleOpenTemplateEditor();
                        }}
                        data-testid="employee-edit-template-menu-edit"
                      >
                        <PencilIcon size={16} />
                        <span>Редактировать</span>
                      </button>
                      <button
                        type="button"
                        className="employee-edit-page__template-menu-item"
                        onClick={() => {
                          setIsTemplateMenuOpen(false);
                          handleCreateNewTemplate();
                        }}
                        data-testid="employee-edit-template-menu-create"
                      >
                        <PlusIcon size={16} />
                        <span>Создать новый шаблон</span>
                      </button>
                    </div>
                  }
                  data-testid="employee-edit-template-menu"
                />
              </div>
            </section>

            <section className="employee-edit-page__section">
              {scheme && (
                <PaymentSchemeEditor
                  fixPerShift={scheme.fix_per_shift}
                  hourlyRate={scheme.hourly_rate}
                  services={scheme.services}
                  disabled={employeePaymentSchemeStore.isSavingDraft}
                  onFixPerShiftBlur={(value) =>
                    setPaymentDraft((prev) => ({ ...prev, fixPerShift: value }))
                  }
                  onHourlyRateBlur={(value) =>
                    setPaymentDraft((prev) => ({ ...prev, hourlyRate: value }))
                  }
                  onServiceRateTypeChange={(
                    serviceUuid: string,
                    rateType: PaymentSchemeRateType,
                    value: number,
                  ) => {
                    // Клампинг (например, до 100% при переключении на
                    // "Процент") уже сделан в самом редакторе — здесь просто
                    // фиксируем то же значение, что видно в поле.
                    setPaymentDraft((prev) => ({
                      ...prev,
                      services: {
                        ...prev.services,
                        [serviceUuid]: { rateType, value },
                      },
                    }));
                  }}
                  onServiceValueBlur={(
                    serviceUuid: string,
                    rateType: PaymentSchemeRateType,
                    value: number | null,
                  ) => {
                    // Бэк не принимает null для ставки по услуге (только для
                    // фикс/часа) — явную очистку поля коммитим как 0.
                    setPaymentDraft((prev) => ({
                      ...prev,
                      services: {
                        ...prev.services,
                        [serviceUuid]: { rateType, value: value ?? 0 },
                      },
                    }));
                  }}
                />
              )}
            </section>
          </>
        )}
      </div>

      <AppConfirmDialog
        isOpen={isDeleteModalOpen}
        title="Удалить сотрудника?"
        message={`Вы действительно хотите удалить сотрудника «${`${form.lastName} ${form.firstName}`.trim()}»? Это действие нельзя будет отменить.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        isConfirming={isDeleting}
        confirmingText="Удаление..."
        data-testid="confirm-delete-employee-modal"
      />

      <AppConfirmDialog
        isOpen={pendingTemplateOption !== undefined}
        title="Сменить шаблон оплаты?"
        message="Несохранённые изменения ставок не были отправлены и будут потеряны. Продолжить?"
        confirmText="Сменить"
        cancelText="Отмена"
        onConfirm={handleConfirmTemplateChange}
        onCancel={() => setPendingTemplateOption(undefined)}
        isConfirming={employeePaymentSchemeStore.isApplyingTemplate}
        confirmingText="Применение..."
        dismissOnOverlayClick={false}
        data-testid="confirm-template-change-modal"
      />
    </div>
  );
});

EmployeeEditPage.displayName = "EmployeeEditPage";

export default EmployeeEditPage;
